import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Radio,
  RadioGroup,
} from "@mui/material";
import JoyButton from "@mui/joy/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Popover from "@mui/material/Popover";
import { propertyTypes } from "../../util/flStates";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FaTimes, FaSearch } from "react-icons/fa"; // Import icons
import styles from "./PropertyFilter.module.css";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const PropertyFilter = ({
  onApplyFilters,
  onDataSourceChange,
  fetchMlsListings,
  fetchFirstLandListings,
}) => {
  const [filters, setFilters] = useState({
    priceMin: "",
    priceMax: "",
    beds: "",
    baths: "",
    type: [],
    zip: "",
    city: "",
    state: "",
    addressInput: "",
    keyword: "",
    professional: "",
    listedBy: "",
    dataSource: "firstLand",
  });

  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [popoverState, setPopoverState] = useState({
    price: null,
    beds: null,
    baths: null,
    type: null,
    listedBy: null,
  });

  useEffect(() => {
    if (window.google?.maps?.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressRef.current,
        {
          types: ["(regions)"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address"],
        }
      );

      autocompleteRef.current.addListener("place_changed", handlePlaceChanged);
    }
  }, []);

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place?.address_components) {
      let updatedFilters = { ...filters };
      let zip = "",
        city = "",
        state = "";

      place.address_components.forEach((component) => {
        const type = component.types[0];
        if (type === "postal_code") zip = component.long_name;
        if (type === "locality") city = component.long_name;
        if (type === "administrative_area_level_1")
          state = component.short_name;
      });

      updatedFilters = {
        ...updatedFilters,
        addressInput: place.formatted_address,
        zip,
        city,
        state,
      };
      setFilters(updatedFilters);
      onApplyFilters(updatedFilters, { triggerApi: true });
    }
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    const updated = { ...filters, addressInput: input };
    setFilters(updated);
    onApplyFilters(updated, { triggerApi: true });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const value = parseInt(e.target.value);
    setFilters((prev) => {
      const newType = prev.type.includes(value)
        ? prev.type.filter((t) => t !== value)
        : [...prev.type, value];
      return { ...prev, type: newType };
    });
  };

  const handleFilterChange = (field) => {
    if (["price", "beds", "baths", "type", "listedBy"].includes(field)) {
      onApplyFilters(filters);
    }

    if (field === "dataSource") {
      if (filters.dataSource === "mls") {
        fetchMlsListings();
      } else if (filters.dataSource === "firstLand") {
        fetchFirstLandListings();
      }
    }
  };

  const handleDataSourceChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      dataSource: value,
    }));
  };

  const handleFilterRemove = (field) => {
    const newFilters = { ...filters, [field]: "" };
    setFilters(newFilters);

    if (["priceMin", "priceMax", "beds", "baths", "listedBy"].includes(field)) {
      onApplyFilters(filtnewFiltersers, { triggerApi: false });
    }
  };

  const handleTypeFilterRemove = (typeId) => {
    const newFilters = {
      ...filters,
      type: filters.type.filter((id) => id !== typeId),
    };
    setFilters(newFilters);
    onApplyFilters(newFilters, { triggerApi: false });
  };

  const handleClick = (e, filter) => {
    setPopoverState({ ...popoverState, [filter]: e.currentTarget });
  };

  const handleClose = (filter) => {
    setPopoverState({ ...popoverState, [filter]: null });
  };

  const handleListedByChange = (e) => {
    setFilters({ ...filters, listedBy: e.target.value });
  };

  const clearInput = () => {
    const updated = { ...filters, addressInput: "" };
    setFilters(updated);
    onApplyFilters(updated, { triggerApi: false });
  };

  const resetFilters = (e) => {
    e.preventDefault();
    const resetState = {
      priceMin: "",
      priceMax: "",
      beds: "",
      baths: "",
      type: [],
      zip: "",
      city: "",
      state: "",
      addressInput: "",
      keyword: "",
      professional: "",
      listedBy: "",
    };
    setFilters(resetState);
    onApplyFilters(resetState, { triggerApi: true });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddressChange(e);
    }
  };

  const performSearch = () => {
    const { addressInput } = filters;
    const zipRegex = /^\d{5}(?:[-\s]\d{4})?$/; // US ZIP code pattern
    const stateRegex = /^[a-zA-Z]{2}$/; // State code pattern (2 letters)
    const isNumeric = /^\d+$/; // Check if input is purely numeric

    const hasMultipleComponents = /\s/.test(addressInput.trim());

    let updatedFilters = { ...filters };

    if (hasMultipleComponents) {
      updatedFilters = {
        ...updatedFilters,
        zip: "",
        city: "",
        state: "",
        addressInput: addressInput.trim(),
      };
    } else if (zipRegex.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        zip: addressInput,
        city: "",
        state: "",
        addressInput: addressInput,
      };
    } else if (stateRegex.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        state: addressInput.toUpperCase(),
        zip: "",
        city: "",
        addressInput: addressInput,
      };
    } else if (!isNumeric.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        city: addressInput,
        zip: "",
        state: "",
        addressInput: addressInput,
      };
    } else {
      updatedFilters = {
        ...updatedFilters,
        zip: "",
        city: "",
        state: "",
        addressInput: "",
      };
    }

    setFilters(updatedFilters);
    onApplyFilters(updatedFilters, { triggerApi: true });
  };

  return (
    <div className="filter-section">
      <Box>
        <form noValidate autoComplete="off">
          <Grid container spacing={2} alignItems="center">
            <Grid item key="address-search">
              <div className={styles.addressSearchBox}>
                <input
                  ref={addressRef}
                  type="text"
                  value={filters.addressInput}
                  onChange={handleAddressChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter ZIP, City, or State"
                  className={styles.addressInput}
                />
                {filters.addressInput && (
                  <div
                    type="button"
                    className={styles.addressRemoveButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearInput();
                    }}
                  >
                    <FaTimes />
                  </div>
                )}
                <div
                  type="button"
                  className={styles.addressSearchButton}
                  onClick={performSearch}
                >
                  <FaSearch />
                </div>
              </div>
            </Grid>

            <Grid item key="listedBy">
              <JoyButton
                color="neutral"
                variant={popoverState.listedBy ? "solid" : "soft"}
                onClick={(event) => handleClick(event, "listedBy")}
                endDecorator={
                  popoverState.listedBy ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
              >
                Listed By
              </JoyButton>
              <Popover
                open={Boolean(popoverState.listedBy)}
                anchorEl={popoverState.listedBy}
                onClose={() => handleClose("listedBy")}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <div className={styles.popBody}>
                  <RadioGroup
                    name="listedBy"
                    value={filters.listedBy || ""}
                    onChange={handleListedByChange}
                  >
                    <FormControlLabel
                      value="professional"
                      control={<Radio />}
                      label="Listed by Professional"
                    />
                    <FormControlLabel
                      value="notProfessional"
                      control={<Radio />}
                      label="Not Listed by Professional"
                    />
                  </RadioGroup>
                </div>
                <div className={styles.popFooter}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleClose("listedBy");
                      handleFilterChange("listedBy");
                    }}
                    fullWidth
                  >
                    Apply
                  </Button>
                </div>
              </Popover>
            </Grid>

            {["price", "beds", "baths", "type"].map((filter) => (
              <Grid item key={filter}>
                <JoyButton
                  color="neutral"
                  variant={popoverState[filter] ? "solid" : "soft"}
                  onClick={(event) => handleClick(event, filter)}
                  endDecorator={
                    popoverState[filter] ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )
                  }
                >
                  {filter === "type"
                    ? "Home Type"
                    : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </JoyButton>
                <Popover
                  open={Boolean(popoverState[filter])}
                  anchorEl={popoverState[filter]}
                  onClose={() => handleClose(filter)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <>
                    {filter === "price" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Price Range</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Minimum"
                            name="priceMin"
                            type="number"
                            value={filters.priceMin}
                            onChange={handleChange}
                            placeholder="No Min"
                            inputProps={{ min: 1 }}
                          />
                          <RemoveIcon sx={{ mx: 1 }} />
                          <TextField
                            label="Maximum"
                            name="priceMax"
                            type="number"
                            value={filters.priceMax}
                            onChange={handleChange}
                            placeholder="No Max"
                            inputProps={{ min: 1 }}
                          />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("price");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "beds" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Number Of Bedrooms</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Bedrooms"
                            name="beds"
                            type="number"
                            value={filters.beds}
                            onChange={handleChange}
                            placeholder="Any"
                            inputProps={{ min: 0 }}
                          />
                          <AddIcon sx={{ mx: 1 }} />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("beds");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "baths" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Number Of Bathrooms</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Bathrooms"
                            name="baths"
                            type="number"
                            value={filters.baths}
                            onChange={handleChange}
                            placeholder="Any"
                            inputProps={{ min: 1 }}
                          />
                          <AddIcon sx={{ mx: 1 }} />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("baths");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "type" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Home Type</div>
                        </div>
                        <div className="pop-body multi-check">
                          {Object.values(propertyTypes).map((propertyType) => (
                            <FormControlLabel
                              key={propertyType.id}
                              control={
                                <Checkbox
                                  checked={filters.type.includes(
                                    propertyType.id
                                  )}
                                  onChange={handleCheckboxChange}
                                  value={propertyType.id}
                                />
                              }
                              label={propertyType.label}
                            />
                          ))}
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("type");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                </Popover>
              </Grid>
            ))}

            <Grid item key="dataSource">
              <JoyButton
                color="neutral"
                variant={popoverState.dataSource ? "solid" : "soft"}
                onClick={(event) => handleClick(event, "dataSource")}
                endDecorator={
                  popoverState.dataSource ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
              >
                Data Source
              </JoyButton>
              <Popover
                open={Boolean(popoverState.dataSource)}
                anchorEl={popoverState.dataSource}
                onClose={() => handleClose("dataSource")}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <div className={styles.popBody}>
                  <RadioGroup
                    name="dataSource"
                    value={filters.dataSource || ""}
                    onChange={handleDataSourceChange}
                  >
                    <FormControlLabel
                      value="firstLand"
                      control={<Radio />}
                      label="First Land"
                    />

                    <FormControlLabel
                      value="mls"
                      control={<Radio />}
                      label="MLS.com®"
                    />
                  </RadioGroup>
                </div>

                <div className={styles.popFooter}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleClose("dataSource");
                      handleFilterChange("dataSource");
                    }}
                    fullWidth
                  >
                    Apply
                  </Button>
                </div>
              </Popover>
            </Grid>

            <Grid item key="filters-selected" className="selected-filters">
              {Object.entries(filters).map(([key, value]) => {
                switch (key) {
                  case "priceMin":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={"> " + value + "$"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "priceMax":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={"< " + value + "$"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "beds":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={value + "+ beds"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "baths":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={value + "+ baths"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "address":
                  case "keyword":
                    if (value) {
                      return <div key={key}>{value}</div>;
                    }
                  case "type":
                    if (value.length) {
                      return (
                        <>
                          {value.length ? (
                            value.map((typeId) => (
                              <Chip
                                key={key + "-" + typeId}
                                label={
                                  Object.values(propertyTypes).find(
                                    (type) => type.id === typeId
                                  ).label
                                }
                                variant="outlined"
                                color="error"
                                onClick={() => handleTypeFilterRemove(typeId)}
                                onDelete={() => handleTypeFilterRemove(typeId)}
                              />
                            ))
                          ) : (
                            <></>
                          )}
                        </>
                      );
                    }
                  case "listedBy":
                    if (
                      value === "professional" ||
                      value === "notProfessional"
                    ) {
                      return (
                        <Chip
                          key={key}
                          label={
                            value === "professional"
                              ? "Listed by Professional"
                              : "Not Listed by Professional"
                          }
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                    break;
                  default:
                    return null;
                }
              })}
            </Grid>
            <Grid item key="reset">
              <JoyButton
                color="neutral"
                variant={"soft"}
                onClick={(event) => resetFilters(event)}
                endDecorator={<RefreshIcon />}
              >
                Reset
              </JoyButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </div>
  );
};

export default PropertyFilter;
